import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { getDeviseCourante } from '../../utils/format';

// ============================================================
// Document imprimable — RouLEAU THERMIQUE (80mm) ou A4
// ============================================================
// Rend les MÊMES données dans deux mises en page selon `format` :
//   - 'thermique' : ticket étroit 74mm, police mono (caisse).
//   - 'a4'        : document large structuré (en-tête, tableau, totaux).
//
// La zone imprimée porte l'id `zone-impression` ; le bloc <style> ne
// laisse visible que cette zone à l'impression et fixe la taille de page
// (@page) selon le format.
//
// Props :
//   format     : 'thermique' | 'a4'
//   titre      : ex. "REÇU DE VENTE", "FACTURE", "BON DE COMMANDE"
//   sousTitre  : optionnel (ex. "Magasin")
//   date       : chaîne déjà formatée
//   reference  : optionnel (ex. "N° BC-2026-0001")
//   client     : optionnel { nom, telephone, adresse }
//   lignes     : [{ nom, qte, pu, total }]
//   extras     : optionnel [{ label, valeur }] (lignes avant le TOTAL : frais, etc.)
//   totalLabel : défaut "TOTAL"
//   total      : nombre
//   apresTotal : optionnel [{ label, valeur }] (paiement, etc.)
//   piedMessage: optionnel (ex. "Merci pour votre achat !")
// ============================================================

function gnf(n) { return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante(); }

export function SelecteurFormat({ format, onChange }) {
  const opt = [['thermique', '🧾 Rouleau'], ['a4', '📄 A4']];
  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 print:hidden">
      {opt.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            format === v ? 'bg-yellow-500 text-black' : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function DocumentImprimable({
  format = 'thermique',
  titre = 'DOCUMENT',
  sousTitre,
  date,
  reference,
  client,
  lignes = [],
  extras = [],
  totalLabel = 'TOTAL',
  total = 0,
  apresTotal = [],
  piedMessage,
}) {
  const { nomCommerce, logoUrl, commerce } = useAuth();
  const nomEnt = nomCommerce || 'Commerce';
  const adresse = commerce?.adresse;
  const tel = commerce?.telephone;
  const totalPcs = lignes.reduce((s, l) => s + Number(l.qte || 0), 0);

  // ── Rendu THERMIQUE (74mm) ──────────────────────────────────
  const Thermique = (
    <div id="zone-impression" className="doc-thermique p-3 font-mono text-xs text-gray-900" style={{ width: '100%', maxWidth: '74mm', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        {logoUrl && <img src={logoUrl} alt="" style={{ width: 40, height: 40, objectFit: 'contain', margin: '0 auto 4px' }} onError={e => { e.target.style.display = 'none'; }} />}
        <div style={{ fontWeight: 900, fontSize: 13, letterSpacing: 1 }}>{nomEnt.toUpperCase()}</div>
        {sousTitre && <div style={{ fontSize: 10, color: '#555' }}>{sousTitre}</div>}
        {date && <div style={{ fontSize: 10, color: '#777' }}>{date}</div>}
      </div>
      <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 10, color: '#111' }}>
        <colgroup><col /><col style={{ width: 22 }} /><col style={{ width: 58 }} /><col style={{ width: 62 }} /></colgroup>
        <thead>
          <tr style={{ fontSize: 9, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
            <th style={{ textAlign: 'left', padding: '0 0 3px 0' }}>Article</th>
            <th style={{ textAlign: 'center', padding: '0 2px 3px' }}>Qté</th>
            <th style={{ textAlign: 'right', padding: '0 2px 3px' }}>P.U.</th>
            <th style={{ textAlign: 'right', padding: '0 0 3px 2px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={i} style={{ verticalAlign: 'top' }}>
              <td style={{ padding: '2px 4px 2px 0', wordWrap: 'break-word', overflowWrap: 'break-word' }}>{l.nom}</td>
              <td style={{ padding: 2, textAlign: 'center' }}>{l.qte}</td>
              <td style={{ padding: 2, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(l.pu || 0).toLocaleString('fr-FR')}</td>
              <td style={{ padding: '2px 0 2px 2px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{Number(l.total ?? l.qte * l.pu).toLocaleString('fr-FR')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#555' }}>
        <span>Articles</span><span>{totalPcs} pcs</span>
      </div>
      {extras.map((e, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#555' }}>
          <span>{e.label}</span><span>{e.valeur}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: 14, color: '#000', marginTop: 4, paddingTop: 4, borderTop: '1px solid #000' }}>
        <span>{totalLabel}</span><span>{gnf(total)}</span>
      </div>
      {apresTotal.map((e, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#555', marginTop: 3 }}>
          <span>{e.label}</span><span>{e.valeur}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
      {piedMessage && <div style={{ textAlign: 'center', fontSize: 10, color: '#555' }}>{piedMessage}</div>}
      {reference && <div style={{ textAlign: 'center', fontSize: 10, color: '#777' }}>{reference}</div>}
      <div style={{ textAlign: 'center', fontSize: 9, color: '#aaa', marginTop: 4 }}>{nomEnt}</div>
    </div>
  );

  // ── Rendu A4 ────────────────────────────────────────────────
  const A4 = (
    <div id="zone-impression" className="doc-a4 text-gray-900" style={{ width: '100%', padding: '4mm', fontFamily: 'Arial, sans-serif' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #111', paddingBottom: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ width: 54, height: 54, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />}
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>{nomEnt}</div>
            {adresse && <div style={{ fontSize: 11, color: '#555' }}>{adresse}</div>}
            {tel && <div style={{ fontSize: 11, color: '#555' }}>Tél : {tel}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 900, fontSize: 18, color: '#111' }}>{titre}</div>
          {sousTitre && <div style={{ fontSize: 12, color: '#555' }}>{sousTitre}</div>}
          {date && <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>{date}</div>}
          {reference && <div style={{ fontSize: 11, color: '#777' }}>{reference}</div>}
        </div>
      </div>

      {/* Client (facture) */}
      {client && (client.nom || client.telephone || client.adresse) && (
        <div style={{ marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', marginBottom: 2 }}>Client</div>
          {client.nom && <div style={{ fontWeight: 700 }}>{client.nom}</div>}
          {client.telephone && <div style={{ color: '#555' }}>{client.telephone}</div>}
          {client.adresse && <div style={{ color: '#555' }}>{client.adresse}</div>}
        </div>
      )}

      {/* Tableau */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
            <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb' }}>Article</th>
            <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'center', width: 60 }}>Qté</th>
            <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', width: 120 }}>Prix unitaire</th>
            <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', width: 130 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={i}>
              <td style={{ padding: '7px 10px', border: '1px solid #e5e7eb' }}>{l.nom}</td>
              <td style={{ padding: '7px 10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{l.qte}</td>
              <td style={{ padding: '7px 10px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{Number(l.pu || 0).toLocaleString('fr-FR')}</td>
              <td style={{ padding: '7px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{Number(l.total ?? l.qte * l.pu).toLocaleString('fr-FR')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totaux */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <div style={{ width: 280, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: '#555' }}>
            <span>Articles</span><span>{totalPcs} pcs</span>
          </div>
          {extras.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: '#555' }}>
              <span>{e.label}</span><span>{e.valeur}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: 4, borderTop: '2px solid #111', fontWeight: 900, fontSize: 16 }}>
            <span>{totalLabel}</span><span>{gnf(total)}</span>
          </div>
          {apresTotal.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: '#555' }}>
              <span>{e.label}</span><span>{e.valeur}</span>
            </div>
          ))}
        </div>
      </div>

      {piedMessage && (
        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: '#555', borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
          {piedMessage}
        </div>
      )}
    </div>
  );

  return (
    <>
      {format === 'a4' ? A4 : Thermique}
      <style>{`
        @media print {
          @page { size: ${format === 'a4' ? 'A4 portrait' : '80mm auto'}; margin: ${format === 'a4' ? '12mm' : '2mm 3mm'}; }
          body * { visibility: hidden; }
          #zone-impression, #zone-impression * { visibility: visible; }
          #zone-impression {
            position: fixed; top: 0; left: 0; background: #fff; color: #000;
          }
          #zone-impression.doc-thermique {
            width: 74mm; max-width: 74mm;
            font-family: 'Courier New', Courier, monospace;
          }
          #zone-impression.doc-a4 { width: 100%; }
        }
      `}</style>
    </>
  );
}
